<template>
    <TransitionRoot as="template" :show="modelValue">
        <Dialog as="div" class="relative z-100" :style="dialogStyle" @close="closeModal">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-white/30 backdrop-blur-xs" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4 text-center vsm:items-center vsm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 vsm:translate-y-0 vsm:scale-95"
                        enter-to="opacity-100 translate-y-0 vsm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 vsm:scale-100"
                        leave-to="opacity-0 translate-y-4 vsm:translate-y-0 vsm:scale-95"
                    >
                        <DialogPanel
                            class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all vsm:my-8 vsm:w-full vsm:max-w-sm vsm:p-6 max-w-lg"
                            :style="'font-family: var(--font);'"
                        >
                            <div v-if="cornerClose" class="absolute -right-2 -top-2 -ml-8 flex pr-2 pt-4 sm:-ml-10 sm:pr-4">
                                <button
                                    type="button"
                                    class="relative rounded-md text-platform-black hover:text-(--brand-colour) focus:outline-none focus:ring-2 focus:ring-white"
                                    @click="closeModal"
                                >
                                    <span class="sr-only">Close panel</span>
                                    <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div>
                                <div
                                    v-if="modelIcon"
                                    class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100"
                                >
                                    <CheckIcon class="h-6 w-6 text-green-600" aria-hidden="true" />
                                </div>
                                <div class="mt-3 vsm:mt-5">
                                    <DialogTitle
                                        v-if="modalTitle"
                                        as="h3"
                                        class="text-fl-lg font-bold leading-6 text-gray-900"
                                        :style="'font-family: var(--heading-font, var(--font));'"
                                    >
                                        <span v-html="modalTitle" />
                                    </DialogTitle>
                                    <div class="modal-body mt-2 text-fl-base" v-html="modalBody" />
                                </div>
                            </div>
                            <div v-if="!cornerClose" class="mt-5 vsm:mt-6 flex justify-center">
                                <PrimaryButtonPlatform
                                    type="button"
                                    :button-text="modalButton"
                                    :click-action="closeModal"
                                />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup>
import { computed } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { CheckIcon } from '@heroicons/vue/24/outline'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import PrimaryButtonPlatform from '@/Components/PrimaryButtonPlatform.vue'

const props = defineProps({
    modalTitle: {
        type: String,
        default: undefined,
    },
    modalBody: {
        type: String,
        default: undefined,
    },
    modalButton: { type: String, default: 'Close' },
    modelValue: Boolean,
    modelIcon: Boolean,
    cornerClose: { type: Boolean, default: false },
    brand: {
        type: Object,
        default: undefined,
    },
    headingFont: {
        type: String,
        default: '',
    },
    bodyFont: {
        type: String,
        default: '',
    },
})

const emit = defineEmits(['update:modelValue'])

const dialogStyle = computed(() => {
    let style = '--brand-colour: ' + props.brand.colour + '; --brand-colour-secondary: ' + (props.brand.colour_secondary || props.brand.colour) + '; --brand-colour-tertiary: ' + (props.brand.colour_tertiary || props.brand.colour) + ';'
    if (props.bodyFont) {
        style += " --font: '" + props.bodyFont + "';"
    }
    if (props.headingFont) {
        style += " --heading-font: '" + props.headingFont + "';"
    }
    return style
})

function closeModal() {
    emit('update:modelValue', false)
}

</script>

<style scoped>
  .tiptap-list-item p {
    display: inline;
  }
</style>